import { useEffect, useState } from "react";
import Modal from "../../shared/components/Modal";
import { toast } from "react-toastify";
import appApi from "../../api";

/**
 * @typedef {{
 *  id: number,
 *  name: string;
 *  collector_key: string;
 *  csv: File;
 * }} UploadFormData
 */

/** @type {UploadFormData} */
const DEFAULT_UPLOAD_FORM = {
    id: undefined, // Only need the ID for editing
    name: "",
};

/**
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.saveButtonText
 * @param {(upload: object) => void} props.onUploadUpdated Called when a bulk upload is updated.
 * @param {() => void} props.onClose
 * @param {boolean} props.isOpen Whether the dialog should be open or not.
 */
function UpdateBulkGeneratorDialog({
    title,
    saveButtonText,
    onUploadUpdated,
    onClose,
    bulkUploadToUpdate,
    isOpen,
}) {
    /** @type {[UploadFormData, React.Dispatch<React.SetStateAction<UploadFormData>>]} */
    const [uploadForm, setUploadForm] = useState({ ...DEFAULT_UPLOAD_FORM });

    useEffect(() => {
        setUploadForm(bulkUploadToUpdate ? { ...bulkUploadToUpdate } : { ...DEFAULT_UPLOAD_FORM });
    }, [bulkUploadToUpdate]);

    function validateForm() {
        const errors = [];

        if (!uploadForm.name) {
            errors.push("Upload name is required");
        }

        return errors;
    }

    async function onSaveUpload() {
        let closeModal = true;

        const errors = validateForm();
        if (errors.length > 0) {
            toast.error(
                <>
                    {errors.length === 1 ? (
                        <>{errors[0]}</>
                    ) : (
                        <ul style={{ marginLeft: "24px" }}>
                            {errors.map((error, index) => (
                                <li key={`generate-collector-error-${index}`} style={{ listStyleType: "disc" }}>
                                    {error}
                                </li>
                            ))}
                        </ul>
                    )}
                </>,
            );
            return;
        }

        try {
            const updatedUpload = await updateUpload();
            onUploadUpdated?.(updatedUpload);
        } catch (error) {
            console.error(error);

            closeModal = false; // Keep the modal open for better UX

            if (error.response?.status === 409) {
                toast.error("A bulk upload with this name already exists");
            } else {
                toast.error(error.response?.data?.message ?? "Failed to generate bulk upload");
            }
        } finally {
            if (closeModal) {
                close();
            }
        }
    }

    async function updateUpload() {
        await appApi.patch("/company/bulk-upload", [uploadForm]);
        return uploadForm;
    }

    function close() {
        onClose?.();
        resetForm();
    }

    function resetForm() {
        setUploadForm({ ...DEFAULT_UPLOAD_FORM });
    }

    return (
        <Modal
            title={title}
            isOpen={isOpen}
            onCancel={() => {
                close();
            }}
            onClickOutside={(_) => {}}
            customButtonProps={[
                {
                    children: saveButtonText,
                    onClick: onSaveUpload,
                    disabled: !uploadForm.name,
                },
            ]}
            style={{
                width: "calc(15% + 200px)",
                minWidth: "fit-content",
                maxWidth: "100%",
            }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div>
                    <label
                        htmlFor="collector-name"
                        className="paragraph-5-semibold"
                        style={{ color: "var(--colors-primary-slate-700)" }}
                    >
                        Name
                    </label>
                    <input
                        id="upload-name"
                        type="text"
                        className="text-field"
                        value={uploadForm.name}
                        onChange={(e) => {
                            setUploadForm({ ...uploadForm, name: e.target.value });
                        }}
                        style={{ width: "100%", color: "var(--colors-primary-slate-900)" }}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default UpdateBulkGeneratorDialog;
